import { useQuery, useMutation } from '@tanstack/react-query-v4';
import {
  ModelEvalRunDTO,
  ModelEvalRunMetadataMapping,
  CreateEvalRunRequest,
  RunDeliveryInfo,
  RunSlicingConfig,
  RunSlicingEditRequest,
  CreateExistingRunRequest,
  ExistingRunMappingResult,
  RunFromDeliveryRequest,
  WorkspaceMapping,
} from '@scale/shared/modelEval';
import EnrichedError from 'react-scale/lib/enrichedError';
import qs from 'query-string';
import apiFetch, { ApiFetchOptions, HTTPError } from 'react-scale/lib/api-fetch';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const BASE_KEY = 'modelEvalCorpClient';

async function evalApiFetch<R = any, B = any>(
  url: string,
  options: ApiFetchOptions<B> = {},
): Promise<R> {
  try {
    return await apiFetch<R, B>(null, url, options);
  } catch (err) {
    if (err instanceof HTTPError) {
      // see tryParseErrorResponse() for how err.response.error gets populated
      throw new EnrichedError(
        err.status ?? 400,
        err.response.error,
        err.response.error,
        err.response.error,
        url,
      );
    } else {
      throw err;
    }
  }
}

export function useRunByIdQuery(runId: string) {
  return useQuery<ModelEvalRunDTO>({
    queryKey: [BASE_KEY, 'run', runId],
    queryFn: async () => {
      const run = await evalApiFetch(`corp-api/eval-run/run/${runId}`, {
        method: 'GET',
      });
      return run;
    },
    enabled: !!runId,
  });
}

export function useRunMetadataVisibilityMutation(runId: string) {
  return useMutation<void, unknown, ModelEvalRunMetadataMapping[]>({
    mutationFn: async (metadataMapping: ModelEvalRunMetadataMapping[]) => {
      await evalApiFetch(`corp-api/eval-run/run/${runId}/update_metadata_visibility`, {
        method: 'POST',
        body: metadataMapping,
      });
    },
  });
}

export function useIsEvalProject(projectId: string) {
  const forceEvalCheck = useUnleashFeatureFlag('force-eval-check', {}, false);
  return useQuery<boolean, Error>({
    queryKey: ['useIsEvalProject', projectId],
    queryFn: async () => {
      if (!forceEvalCheck) {
        return true;
      }
      const result = await evalApiFetch(`corp-api/delivery-builder/is-eval-project`, {
        query: { projectId },
      });
      return result?.result ?? false;
    },
  });
}

export function useGetMappingsFromWorkspaceQuery(workspaceId: string, taskId: string) {
  return useQuery<WorkspaceMapping | null>({
    queryKey: ['getMappingsFromWorkspace', workspaceId, taskId],
    queryFn: async () => {
      if (!workspaceId || !taskId) {
        return null;
      }
      const result = await evalApiFetch(`corp-api/rapid/workspaceInfo`, {
        method: 'POST',
        body: { workspaceId, taskId },
      });
      return result;
    },
  });
}

export function useGetSliceableMetadataQuery(taskIds: string[]) {
  return useQuery<Record<string, string> | null>({
    queryKey: ['getSliceableMetadata', taskIds],
    queryFn: async () => {
      if (!taskIds.length) {
        return null;
      }
      const result = await evalApiFetch(`corp-api/rapid/sliceableMetadata`, {
        method: 'POST',
        body: { taskIds },
      });
      return result;
    },
  });
}

export function useCreateEvalRun() {
  return useMutation<
    { customerId: string; runId: string; workspaceId: string },
    Error,
    CreateEvalRunRequest
  >({
    mutationFn: async body => {
      if (!body.isManualEvalRun && body.deliveryBatchId) {
        // use delivery batch eval run autocreation
        const result = await evalApiFetch(`corp-api/eval-run/run_from_delivery_batch`, {
          method: 'POST',
          body: {
            name: body.runName,
            projectId: body.projectId,
            taskIds: body.taskIds,
            deliveryBatchId: body.deliveryBatchId,
          },
        });
        return result;
      }
      const result = await evalApiFetch(`corp-api/rapid/generateEvalRun`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}

export function useGetEvalRuns({ projectId }: { projectId: string }) {
  return useQuery<RunDeliveryInfo[]>({
    queryKey: [BASE_KEY, 'getProjectEvalRuns', projectId] as const,
    queryFn: async () => {
      // Library omits undefined keys by default
      const queryString = qs.stringify({
        projectId,
      });

      const { runs } = await evalApiFetch(`corp-api/delivery-builder/eval_runs?${queryString}`);
      return runs;
    },
  });
}

export function useGetEvalRunInfo(runId?: string) {
  return useQuery<RunDeliveryInfo>({
    queryKey: ['getEvalRunInfo', runId],
    queryFn: async () => {
      const { run } = await evalApiFetch(`corp-api/delivery-builder/get_eval_run_info/${runId}`);
      return run;
    },
    enabled: !!runId,
  });
}

export function useGetArchivedRuns({ deliveryBatchId }: { deliveryBatchId: string }) {
  return useQuery<string[]>({
    queryKey: ['getArchivedRuns', deliveryBatchId] as const,
    queryFn: async () => {
      // Library omits undefined keys by default
      const queryString = qs.stringify({
        deliveryBatchId,
      });

      const { runs } = await evalApiFetch(
        `corp-api/delivery-builder/archived_eval_runs?${queryString}`,
      );
      return runs;
    },
  });
}

export function useRunFinalizationMutation() {
  return useMutation<any, string, { runId: string }>({
    mutationFn: async ({ runId }) => {
      const result = await evalApiFetch(`corp-api/delivery-builder/finalize_run/${runId}`, {
        method: 'POST',
      });
      return result;
    },
  });
}

export function useRunArchiveMutation() {
  return useMutation<{}, string, { runId: string }>({
    mutationFn: async ({ runId }) => {
      const result = await evalApiFetch(`corp-api/delivery-builder/archive_run/${runId}`, {
        method: 'POST',
      });
      return result;
    },
  });
}
export function useRunEditMutation() {
  return useMutation<any, string, RunSlicingEditRequest>({
    mutationFn: async ({ slicingFields, testCasesFields, runId }) => {
      const result = await evalApiFetch(`corp-api/rapid/configureNonMetric`, {
        method: 'POST',
        body: { slicingFields, testCasesFields, runId },
      });
      return result;
    },
  });
}

export function useRunSlicingConfig({ runId }: { runId: string }) {
  return useQuery<RunSlicingConfig>({
    queryKey: ['runMappingInfo', runId],
    queryFn: async () => {
      const result = await evalApiFetch(`corp-api/rapid/runMappingInfo`, {
        method: 'POST',
        body: { runId },
      });
      return result;
    },
  });
}

export function useGetExistingQualEvalRun({ allTaskIds }: { allTaskIds: string[] }) {
  return useQuery<{ runId: string | undefined; errorMessage: string | undefined }>({
    queryKey: ['getExistingRun', allTaskIds],
    queryFn: async () => {
      const result = await evalApiFetch(`corp-api/delivery-builder/existing_qual_eval_run`, {
        method: 'POST',
        body: { allTaskIds },
      });
      return result;
    },
  });
}

export function useGetMappingsFromExistingRunQuery(runId: string, taskId: string) {
  return useQuery<ExistingRunMappingResult | null>({
    queryKey: ['getMappingsFromExistingRun', runId, taskId],
    queryFn: async () => {
      if (!runId) {
        return null;
      }
      const result = await evalApiFetch(`corp-api/rapid/existingRunInfo`, {
        method: 'POST',
        body: { runId, taskId },
      });
      return result;
    },
  });
}

export function useCreateExistingEvalRun() {
  return useMutation<
    { customerId: string; runId: string; workspaceId: string },
    Error,
    CreateExistingRunRequest
  >({
    mutationFn: async body => {
      const result = await evalApiFetch(`corp-api/rapid/generateExistingEvalRun`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}

export function useCanCreateEvalRun({
  batchId,
  taskId,
}: {
  batchId: string | undefined;
  taskId: string | undefined;
}) {
  return useQuery<boolean>({
    queryKey: ['canCreateEvalRun', taskId, batchId],
    queryFn: async () => {
      const queryString = qs.stringify({
        ...(batchId ? { batchId } : {}),
        ...(taskId ? { taskId } : {}),
      });
      const result = await evalApiFetch(`corp-api/rapid/canCreateEvalRun?${queryString}`);
      return result;
    },
    enabled: !!taskId || !!batchId,
  });
}

export function useCreateRunFromDeliveryBatch() {
  return useMutation<{}, Error, RunFromDeliveryRequest>({
    mutationFn: async body => {
      const result = await evalApiFetch(`corp-api/eval-run/run_from_delivery_batch`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}
